import { ClientSession } from "./ClientSessionHolder";
import GdprStorage from "../storage/GdprStorage";
import { jwtDecode } from "jwt-decode";
import Tracking from "../tracking/Tracking";
import { makeCookieOptions } from "./SafeCookie";
import * as BettyJwt from "../../model/BettyJwt";
import { UserManager } from "oidc-client-ts";
export class IdamUserManager extends UserManager {
	async storeUser(user: any): Promise<any> {
		//IDAM is not supporting the refresh_token flow when is used PKCE, but oidc defaults to this flow when refresh_token is present
		//removing the refresh_token from the user data will force oidc to go via iframe route and silent refresh will work fine
		if (user && user.refresh_token) {
			delete user.refresh_token;
		}

		return super.storeUser(user);
	}
}

const LoginUtil = {
	isIdamEmployee: (): boolean => {
		return (
			window.location.hostname.endsWith(".metronom.com") ||
			(window.location.href.includes("-pdt.betty") && window.location.href.includes("workeruidispatcher"))
		);
	},
	removeIdamRedirectParam: (uri: string): string => {
		const url = new URL(uri);
		url.searchParams.delete("idamRedirect");
		url.searchParams.delete("_ga");
		url.searchParams.delete("itm_pm");
		return url.toString();
	},
	getOAuthRedirectParams: (state: any): Record<string, any> => {
		const idamRedirect = state.query && state.query.idamRedirect;
		let redirectedLocation = window.location.href;
		// in case an error occured:
		const error = state.query && state.query.error;
		const errorDescription = state.query && state.query.error_description;
		redirectedLocation = redirectedLocation.replace(/[?&#]id_token=[^?&#]*/, "");
		redirectedLocation = redirectedLocation.replace(/[?&]error=[^?&#]*/, "");
		redirectedLocation = redirectedLocation.replace(/[?&]session_state=[^?&#]*/, "");
		redirectedLocation = redirectedLocation.replace(/[?&]access_token=[^?&#]*/, "");
		redirectedLocation = redirectedLocation.replace(/[?&]expires_in=[^?&#]*/, "");
		redirectedLocation = redirectedLocation.replace(/[?&]state=[^?&#]*/, "");
		redirectedLocation = redirectedLocation.replace(/[?&]token_type=[^?&#]*/, "");
		// this is for legacy reasons (to remove old bookmarked login urls)
		redirectedLocation = redirectedLocation.replace(/[?&]code=[^?&#]*/, "");
		return {
			error,
			errorDescription,
			isIdamRedirect: !!idamRedirect,
			redirectUri: redirectedLocation,
		};
	},

	/**
	 * Generates a CallTreeId with the given prefix
	 * and concatinates it with the client session id.
	 */
	generateSessionedCallTreeIdWithPrefix: (prefix: string): string => {
		return ClientSession.generateSessionedCallTreeIdWithPrefix(prefix);
	},
	parseResponseTextToObject: (responseText: string): Record<string, any> | null | undefined => {
		try {
			return JSON.parse(responseText);
		} catch {
			console.error("XMLHttpRequest.responseText could not be parsed to JSON.");
			return undefined;
		}
	},
	persistEmployeeStores: (stores: Record<string, any>) => {
		try {
			const employeeStores = JSON.stringify(stores);
			GdprStorage.localStorage.put("employee_stores", employeeStores, GdprStorage.NECESSARY);
		} catch {
			console.error("Parsing stores from request failed!");
		}
	},
	persistCustomerTierLevel: (jwt: Record<string, any>) => {
		if (BettyJwt.getTierLevel(jwt)) {
			GdprStorage.localStorage.put("customer_tier_level", BettyJwt.getTierLevel(jwt), GdprStorage.NECESSARY);
		}
	},
	getJwtFromCookieAndStorage: (): Jwts => {
		const jwts = {} as Jwts;
		jwts.compressedJwt = GdprStorage.cookie.read("compressedJWT");
		jwts.jwt = GdprStorage.localStorage.read("JWT");
		return jwts;
	},
	removeJwtFromCookieAndStorage: () => {
		GdprStorage.cookie.remove("compressedJWT");
		GdprStorage.localStorage.remove("JWT");
		GdprStorage.cookie.remove("JWT"); //Need to remove that as well, in the migration scenario
	},
	removeEmployeeInfoFromCookieAndStorage: () => {
		GdprStorage.localStorage.remove("employee_profile");
		GdprStorage.localStorage.remove("employee_stores");
	},
	parseJWT: (rawJwt: string | null | undefined): any => {
		try {
			const jwt = jwtDecode(rawJwt ?? "") as any;

			if (jwt?.payload) {
				jwt.payload = JSON.parse(jwt.payload);
			}

			if (jwt?.storeEnts) {
				jwt.storeEnts = JSON.parse(jwt.storeEnts);
			}

			return jwt;
		} catch (error) {
			console.error("could not decode cookie. '" + String(rawJwt) + "'", error);
			Tracking.trackError("platform", "ui", "LoginUtil", "could not decode cookie", rawJwt);
		}
	},
	getUserTypeFromAccessToken: (access_token: string): string | null | undefined => {
		let userType = undefined;

		try {
			const decoded = jwtDecode(access_token) as any;
			userType = decoded.userType;
		} catch (error) {
			Tracking.trackError(
				"platform",
				"ui",
				"LoginUtil",
				"could not decode access token in signin response of previous user. '" +
					JSON.stringify(access_token) +
					"'",
				error,
			);
			console.error(
				"could not decode access token in signin response of previous user. '" +
					JSON.stringify(access_token) +
					"'",
				error,
			);
		}

		return userType;
	},
	getUserEmailFromAccessToken: (access_token: string): string => {
		let userEmail = "";

		try {
			const decodedToken = jwtDecode(access_token) as any;
			userEmail = decodedToken.email;
		} catch (error) {
			Tracking.trackError("platform", "ui", "LoginUtil", "could not decode access token", error);
			console.error("Could not decode access token. Error is: ", error);
		}

		return userEmail;
	},
	getExpireTimestampFromAccessToken: (access_token: string): string => {
		let expiresAtTimestamp = "";

		try {
			const decodedToken = jwtDecode(access_token) as any;
			expiresAtTimestamp = decodedToken.exp;
		} catch (error) {
			Tracking.trackError("platform", "ui", "LoginUtil", "could not decode access token", error);
			console.error("Could not decode access token. Error is: ", error);
		}

		return expiresAtTimestamp;
	},
	getMetroIdFromAccessToken: (access_token: string): string => {
		let metroId = "";

		try {
			const decodedToken = jwtDecode(access_token) as any;
			metroId = decodedToken.metro_id;
		} catch (error) {
			Tracking.trackError("platform", "ui", "LoginUtil", "could not decode access token", error);
			console.error("Could not decode access token. Error is: ", error);
		}

		return metroId;
	},
	getRealmFromIdToken: (id_token: string): string | null | undefined => {
		let realm = undefined;

		try {
			const decoded = jwtDecode(id_token) as any;
			realm = decoded.realm;
		} catch (error) {
			Tracking.trackError(
				"platform",
				"ui",
				"LoginUtil",
				"could not decode id token signin response of previous user. '" + JSON.stringify(id_token) + "'",
				error,
			);
			console.error(
				"could not decode id_token token in signin response of previous user. '" +
					JSON.stringify(id_token) +
					"'",
				error,
			);
		}

		return realm;
	},
	checkUserAndStoreTokens: (user: Record<string, any>): boolean => {
		if (!user || !user.access_token) {
			console.warn("Can not store user access token, because it is not present!");
			return false;
		}

		if (!user || !user.id_token) {
			console.warn("Can not store user id token, because it is not present!");
			return false;
		}

		return LoginUtil.storeTokens(user.access_token, user.id_token);
	},
	storeTokens: (access_token: string, id_token: string): boolean => {
		const accessTokenWasStored = storeUserAccessToken(access_token);
		const idTokenWasStored = storeUserIdToken(id_token);

		if (accessTokenWasStored && idTokenWasStored) {
			console.debug("user tokens successfully set");
		}

		return accessTokenWasStored && idTokenWasStored;
	},
	removeUserTokens: (): boolean => {
		const accessTokenRemoved = GdprStorage.localStorage.remove("idamUserAccessToken");
		const idTokenRemoved = GdprStorage.cookie.remove("idamUserIdToken");
		return accessTokenRemoved && idTokenRemoved;
	},
};

function storeUserAccessToken(token: string): boolean {
	const accessTokenWasSet = GdprStorage.localStorage.put("idamUserAccessToken", token, GdprStorage.NECESSARY);

	if (!accessTokenWasSet) {
		console.error("An error occurred, idamUserAccessToken wasn't set in localstorage!");
	}

	return accessTokenWasSet;
}

function storeUserIdToken(token: string): boolean {
	const idTokenWasSetInCookie = GdprStorage.cookie.put(
		"idamUserIdToken",
		token,
		GdprStorage.NECESSARY,
		makeCookieOptions(),
	);

	if (!idTokenWasSetInCookie) {
		console.error("An error occurred, idamUserIdToken cookie wasn't set!");
	}

	return idTokenWasSetInCookie;
}

export type Jwts = {
	compressedJwt: string | null | undefined;
	jwt: string | null | undefined;
};
export default LoginUtil;
